import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  DollarSign, Workflow, Activity, Home, ChevronRight, Wrench,
  FileSpreadsheet, Code2, BookOpen, Users, CreditCard, Settings, LogOut, Layers,
  Shield, Key, User, BarChart3, Cpu, GitBranch, Zap, PanelLeftClose, PanelLeftOpen,
  TrendingUp, FlaskConical, Building2, ShieldAlert, Layers as LayersIcon, Network, Store, Landmark, Globe2, FolderKanban, Library, Sparkles,
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";
import SavingsCounter from "@/components/SavingsCounter";
import LanguageSelector from "@/components/LanguageSelector";
import ScopeViolationBadge from "@/components/dashboard/ScopeViolationBadge";
import { ProviderIcon } from "@/components/logos";
import { LOGO_ICON } from "@/config/dashboardConstants";

const NAV_ITEMS = [
  // ── Universal Tenant Workspace (Phase 4 shell) ──
  { key: "workspace", label: "Workspace", icon: Sparkles, route: "/app/chat", badge: "NEW" },
  // ── Swarm agents (investor-facing, redacted names) ──
  { key: "haven-swarm", label: "HAVEN", icon: LayersIcon, route: "/haven/swarm" },
  { key: "mens-wearhouse", label: "Retail", icon: Store, route: "/swarm" },
  { key: "jnj-swarm", label: "Pharmaceutical", icon: FlaskConical, route: "/swarm?mode=jnj" },
  { key: "sales-swarm", label: "Brand Management", icon: TrendingUp },
  { key: "rightsline-swarm", label: "Licensing", icon: Globe2, route: "/rightsline" },
  { key: "csh-swarm", label: "Grants", icon: Landmark, route: "/csh/swarm" },
  // ── Platform ──
  { key: "portfolio", label: "Private Equity I", icon: Building2, route: "/portfolio" },
  { key: "portfolio-hf", label: "Private Equity II", icon: Building2, route: "/portfolio/hf" },
  { key: "providers", label: "Provider Rates", icon: DollarSign, route: "/providers" },
  { key: "spend-protection", label: "Spend Protection", icon: ShieldAlert, route: "/spend-protection" },
  { key: "deal-analysis", label: "Deal Analysis", icon: FileSpreadsheet, route: "/deal-analysis" },
  { key: "optimize", label: "Home", icon: Home },
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "cross-vertical", label: "Intelligence", icon: Activity },
  { key: "kb", label: "Knowledge Base", icon: Library },
  { key: "tenant-admin", label: "Tenant Admin", icon: Shield },
  { key: "analytics", label: "Savings", icon: DollarSign },
  { key: "workflows", label: "Workflows", icon: Workflow },
  { key: "model-catalog", label: "Model Catalog", icon: Activity },
  { key: "provider-mgmt", label: "Providers", icon: Layers },
  { key: "optimization-engine", label: "Optimizer", icon: Cpu },
  { key: "swarm", label: "Agent Swarm", icon: GitBranch },
  { key: "status", label: "Status", icon: Shield },
];

const DEV_ITEMS = [
  { key: "haven-portal", label: "HAVEN Portal", icon: Zap },
  { key: "harvest-portal", label: "HARVEST Portal", icon: Zap },
  { key: "models", label: "Gateway Playground", icon: FlaskConical },
  { key: "docs", label: "API Portal", icon: FileSpreadsheet },
  { key: "examples", label: "Code Examples", icon: Code2 },
  { key: "knowledge", label: "Documents", icon: BookOpen },
];

const BOTTOM_ITEMS = [
  { key: "team-mgmt", label: "Team", icon: Users },
];

const ACCOUNT_ITEMS = [
  { key: "settings", label: "Settings", icon: Settings },
  { key: "profile", label: "Profile", icon: User },
  { key: "usage", label: "Usage", icon: BarChart3 },
];

export default function DashboardSidebar({
  activeTab,
  setActiveTab,
  devExpanded,
  setDevExpanded,
  collapsed,
  setCollapsed,
  user,
  solverStatus,
  activeProfile,
  activeProviders,
  onLogout,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const c = collapsed; // shorthand

  const renderNavButton = (item) => {
    let isActive;
    if (item.route) {
      if (item.route.includes("?")) {
        isActive = (location.pathname + location.search) === item.route;
      } else {
        isActive = location.pathname === item.route && !location.search;
      }
    } else {
      isActive = activeTab === item.key;
    }
    const handleClick = item.route ? () => navigate(item.route) : () => setActiveTab(item.key);
    return (
      <motion.button
        key={item.key}
        whileHover={{ x: c ? 0 : 2 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleClick}
        data-testid={`tab-${item.key}`}
        title={c ? item.label : undefined}
        className={`w-full flex items-center ${c ? "justify-center" : ""} gap-3 ${c ? "px-2 py-2.5" : "px-3 py-2.5"} rounded-lg text-sm font-medium transition-all duration-200 ${
          isActive
            ? "bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-l-2 border-[var(--qn-primary)]"
            : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"
        }`}
      >
        <item.icon className={`w-4 h-4 shrink-0 ${isActive ? "text-[var(--qn-primary)]" : ""}`} />
        {!c && item.label}
        {!c && item.badge && (
          <span className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-300 border border-teal-500/30">
            {item.badge}
          </span>
        )}
      </motion.button>
    );
  };

  return (
    <motion.aside
      initial={false}
      animate={{ width: c ? 72 : 240 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed left-0 top-0 bottom-0 z-50 flex flex-col border-r border-[var(--qn-border)] bg-[var(--qn-card)] overflow-hidden"
      data-testid="sidebar"
    >
      {/* Logo + Collapse Toggle */}
      <div className={`px-${c ? "3" : "5"} py-4 border-b border-[var(--qn-border)] ${isDark ? "bg-slate-50 dark:bg-[#0A0E1A]" : "bg-white"} rounded-b-xl flex items-center ${c ? "justify-center" : "justify-between"}`}>
        <div className={`flex items-center gap-3 cursor-pointer ${c ? "" : ""}`} onClick={() => navigate("/")}>
          <motion.img src={LOGO_ICON} alt="QNNEAL" className={`${c ? "h-8 w-8" : "h-10 w-10"} object-contain`} whileHover={{ scale: 1.05 }} />
          {!c && <span className="font-outfit font-bold text-2xl tracking-tight text-white">QNNEAL</span>}
        </div>
        <button
          onClick={() => setCollapsed(!c)}
          className="w-7 h-7 rounded-md flex items-center justify-center text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)] transition-colors"
          data-testid="sidebar-collapse-btn"
          title={c ? "Expand sidebar" : "Collapse sidebar"}
        >
          {c ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* Badges */}
      {!c && (
        <div className="flex items-center gap-1.5 px-5 py-2">
          <span className="text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border border-[var(--qn-primary)]/20" data-testid="agentic-ai-badge">
            Agentic AI
          </span>
          {solverStatus?.quantum_status === "connected" && (
            <span className="text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded bg-[var(--qn-secondary-muted)] text-[var(--qn-secondary)] border border-[var(--qn-secondary)]/20" data-testid="quantum-enhanced-badge">
              Quantum-Enhanced
            </span>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className={`flex-1 ${c ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto`} data-testid="sidebar-nav">
        {user?.is_platform_admin && renderNavButton({ key: "platform-admin", label: "Platform Admin", icon: Shield, route: "/platform-admin", badge: "ROOT" })}
        {NAV_ITEMS.map(renderNavButton)}

        <div className="border-t border-[var(--qn-border)] my-3" />

        {/* Developer Section */}
        {c ? (
          // Collapsed: just show icon buttons for dev items
          <>
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => { setCollapsed(false); setDevExpanded(true); }}
              title="Developer"
              className="w-full flex items-center justify-center px-2 py-2.5 rounded-lg text-sm font-medium text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)] transition-all duration-200"
            >
              <Wrench className="w-4 h-4 shrink-0" />
            </motion.button>
          </>
        ) : (
          <>
            <button
              onClick={() => setDevExpanded((p) => !p)}
              data-testid="developer-section-toggle"
              data-automation-id="nav-developer-toggle"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)] transition-all duration-200"
            >
              <Wrench className="w-4 h-4 shrink-0" />
              Developer
              <motion.div className="ml-auto" animate={{ rotate: devExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronRight className="w-3.5 h-3.5" />
              </motion.div>
            </button>
            <AnimatePresence>
              {devExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  {DEV_ITEMS.map((item) => {
                    const isActive = activeTab === item.key;
                    return (
                      <motion.button
                        key={item.key}
                        whileHover={{ x: 2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setActiveTab(item.key)}
                        data-testid={`tab-${item.key}`}
                        className={`w-full flex items-center gap-3 pl-10 pr-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                          isActive
                            ? "bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] font-medium border-l-2 border-[var(--qn-primary)]"
                            : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"
                        }`}
                      >
                        <item.icon className={`w-4 h-4 shrink-0 ${isActive ? "text-[var(--qn-primary)]" : ""}`} />
                        {item.label}
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        <div className="border-t border-[var(--qn-border)] my-3" />

        {BOTTOM_ITEMS.map(renderNavButton)}

        {!c && (
          <>
            <div className="border-t border-[var(--qn-border)] my-3" />
            <div className="text-[10px] uppercase tracking-wider text-[var(--qn-foreground-muted)] px-3 mb-1 font-semibold">Account</div>
            {ACCOUNT_ITEMS.map(renderNavButton)}
          </>
        )}
      </nav>

      {/* Bottom Section */}
      <div className={`border-t border-[var(--qn-border)] ${c ? "p-2" : "p-3"} space-y-2`}>
        {!c && <SavingsCounter />}

        {!c && activeProfile && (
          <div className="px-3 py-2 rounded-lg bg-[var(--qn-surface)] border border-[var(--qn-border)]/50 mb-1" data-testid="sidebar-routing-profile">
            <div className="text-xs text-[var(--qn-foreground-subtle)] mb-1">Active Profile</div>
            <div className="flex items-center gap-1.5">
              {activeProviders.slice(0, 3).map((p) => (
                <ProviderIcon key={p} providerKey={p} size="sm" variant="color" />
              ))}
              <span className="text-xs font-medium text-[var(--qn-foreground-muted)] truncate">
                {activeProfile.replace(/_/g, " + ").replace(/\b\w/g, (l) => l.toUpperCase()).replace("Only", "").trim()}
              </span>
            </div>
          </div>
        )}

        {c ? (
          <div className="flex flex-col items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/billing")} className="h-8 w-8 text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]" data-testid="billing-btn" title="Plan & Billing">
              <CreditCard className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} className="h-8 w-8 text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]" data-testid="settings-btn" title="Settings">
              <Settings className="w-4 h-4" />
            </Button>
            <div className="py-1">
              <ThemeToggle />
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" title={user?.name} />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--qn-primary)] to-[var(--qn-secondary)] flex items-center justify-center text-sm font-medium text-white" title={user?.name}>
                {user?.name?.charAt(0) || "U"}
              </div>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--qn-foreground-subtle)] hover:text-red-400" onClick={onLogout} data-testid="logout-btn" title="Logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => navigate("/billing")} className="w-full justify-start gap-3 text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]" data-testid="billing-btn" data-automation-id="nav-billing">
              <CreditCard className="w-4 h-4 shrink-0" />
              <span className="truncate">Plan & Billing</span>
            </Button>

            <Button variant="ghost" size="sm" onClick={() => navigate("/settings")} className="w-full justify-start gap-3 text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]" data-testid="settings-btn" data-automation-id="nav-settings">
              <Settings className="w-4 h-4 shrink-0" />
              Settings
            </Button>

            <ScopeViolationBadge collapsed={false} onOpenSettings={() => navigate("/settings")} />

            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-xs text-[var(--qn-foreground-subtle)]">Theme</span>
              <ThemeToggle />
            </div>

            <div className="px-3 py-1.5">
              <LanguageSelector compact />
            </div>

            <div className="flex items-center justify-between p-2 rounded-lg bg-[var(--qn-surface)] mt-1">
              <div className="flex items-center gap-2.5 min-w-0">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--qn-primary)] to-[var(--qn-secondary)] flex items-center justify-center text-sm font-medium text-white shrink-0">
                    {user?.name?.charAt(0) || "U"}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-[var(--qn-foreground-subtle)] truncate">{user?.email}</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-[var(--qn-foreground-subtle)] hover:text-red-400" onClick={onLogout} data-testid="logout-btn" data-automation-id="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </motion.aside>
  );
}
